import React, { useState, useEffect } from "react";
import { adminApi } from "../../api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Plus,
  Trash2,
  Edit,
  ExternalLink,
  Layers,
  Package,
} from "lucide-react";
import { toast } from "sonner";

const NichesPage = () => {
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    category: "AI & Automation",
    meta_title: "",
    meta_description: "",
    hero_image_url: "",
  });

  const fetchNiches = async () => {
    try {
      const res = await adminApi.getNiches();
      setNiches(res.data);
    } catch (error) {
      toast.error("Failed to load niches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNiches();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "name" && {
        slug: value.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
      }),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await adminApi.createNiche(formData);
      toast.success("Niche created successfully!");
      setDialogOpen(false);
      setFormData({
        name: "",
        slug: "",
        description: "",
        category: "AI & Automation",
        meta_title: "",
        meta_description: "",
        hero_image_url: "",
      });
      fetchNiches();
    } catch (error) {
      toast.error("Failed to create niche");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this niche and all its products?")) return;
    try {
      await adminApi.deleteNiche(id);
      toast.success("Niche deleted");
      fetchNiches();
    } catch (error) {
      toast.error("Failed to delete niche");
    }
  };

  const categories = [
    "AI & Automation",
    "Personal Finance",
    "Remote Work",
    "Digital Education",
    "E-commerce",
    "Marketing Tools",
    "Cybersecurity",
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="niches-loading">
        <div className="animate-pulse text-[#A1A1AA]">Loading niches...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="niches-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Niches</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            Manage your affiliate site categories
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#007AFF] hover:bg-[#007AFF]/80 text-white"
              data-testid="add-niche-btn"
            >
              <Plus size={16} className="mr-2" />
              Add Niche
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Niche</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-[#A1A1AA]">Name</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="AI Video Tools"
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  required
                  data-testid="niche-name-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">Slug</label>
                <Input
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="ai-video-tools"
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  required
                  data-testid="niche-slug-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">Category</label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full bg-[#1A1A1A] border border-white/10 text-white rounded-md p-2 mt-1"
                  data-testid="niche-category-select"
                >
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">Description</label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Compare the best AI video generation tools..."
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  rows={3}
                  required
                  data-testid="niche-description-input"
                />
              </div>
              <div>
                <label className="text-sm text-[#A1A1AA]">
                  Hero Image URL (optional)
                </label>
                <Input
                  name="hero_image_url"
                  value={formData.hero_image_url}
                  onChange={handleInputChange}
                  placeholder="https://..."
                  className="bg-[#1A1A1A] border-white/10 text-white mt-1"
                  data-testid="niche-hero-image-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-[#007AFF] hover:bg-[#007AFF]/80"
                data-testid="create-niche-submit-btn"
              >
                Create Niche
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Niches Grid */}
      {niches.length === 0 ? (
        <div className="bg-[#121212] border border-white/10 rounded-lg p-12 text-center">
          <Layers size={48} className="mx-auto text-[#A1A1AA] mb-4" />
          <h3 className="text-white text-lg font-semibold">No niches yet</h3>
          <p className="text-[#A1A1AA] mt-2">
            Create your first niche to start adding products
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {niches.map((niche) => (
            <div
              key={niche.id}
              className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-white/20 transition-all group"
              data-testid={`niche-card-${niche.slug}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <span className="text-xs font-mono text-[#007AFF] uppercase tracking-wider">
                    {niche.category}
                  </span>
                  <h3 className="text-white font-semibold text-lg mt-1">
                    {niche.name}
                  </h3>
                  <p className="text-[#A1A1AA] text-sm mt-2 line-clamp-2">
                    {niche.description}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-[#A1A1AA]">
                  <Package size={14} />
                  <span className="text-sm font-mono">
                    {niche.product_count || 0} products
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={`/${niche.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-[#A1A1AA] hover:text-white rounded-md hover:bg-white/10 transition-colors"
                    data-testid={`view-niche-${niche.slug}-btn`}
                  >
                    <ExternalLink size={16} />
                  </a>
                  <button
                    onClick={() => handleDelete(niche.id)}
                    className="p-2 text-[#A1A1AA] hover:text-red-400 rounded-md hover:bg-white/10 transition-colors"
                    data-testid={`delete-niche-${niche.slug}-btn`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NichesPage;
